import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Gear, ChatCircleDots, Camera, Gift, Crown, Wallet as WalletIcon, Trophy, Coins, Prohibit, DotsThree } from "@phosphor-icons/react";
import GiftModal from "../components/GiftModal";
import VerifiedBadge from "../components/VerifiedBadge";
import ProfileBadges from "../components/ProfileBadges";

export default function Profile() {
  const { username } = useParams();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(false);
  const [bio, setBio] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);
  const [subBusy, setSubBusy] = useState(false);
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/meta/countries").then((r) => setCountries(r.data)).catch(() => {});
  }, []);

  const targetUsername = username || user?.username;
  const isMe = !username || (user && username === user.username);

  const load = useCallback(async () => {
    if (!targetUsername) return;
    try {
      const { data } = await api.get(`/users/${targetUsername}`);
      setProfile(data);
      setBio(data.bio || "");
      setDisplayName(data.display_name || "");
      setCountry(data.country || "");
      const { data: ps } = await api.get(`/users/${targetUsername}/posts`);
      setPosts(ps);
    } catch (err) {
      console.error("profile load failed", err);
    }
  }, [targetUsername]);

  useEffect(() => { load(); }, [load]);

  const toggleFollow = async () => {
    if (!profile) return;
    await api.post(`/users/${profile.id}/follow`);
    load();
  };

  const subscribe = async () => {
    if (subBusy || !profile) return;
    setSubBusy(true);
    try {
      await api.post("/subscriptions", { creator_id: profile.id });
      alert(`You're now subscribed to ${profile.display_name} for 30 days! 🎉`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Subscribe failed. Top up your Coins.");
    } finally { setSubBusy(false); }
  };

  const saveProfile = async () => {
    const form = new FormData();
    form.append("display_name", displayName);
    form.append("bio", bio);
    await api.put("/users/me", form);
    if (country !== (profile.country || "")) {
      const idForm = new FormData();
      idForm.append("country", country);
      await api.put("/users/me/identity", idForm);
    }
    setEditing(false);
    await refresh();
    load();
  };

  const toggleBlock = async () => {
    if (!profile) return;
    if (!window.confirm(`Block @${profile.username}? They won't be able to follow or message you.`)) return;
    await api.post(`/users/${profile.id}/block`);
    setMenuOpen(false);
    nav("/");
  };

  const onAvatarPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAvatarUploading(true);
    try {
      const { uploadMedia } = await import("../lib/uploadMedia");
      const uploaded = await uploadMedia(f);
      const update = new FormData();
      update.append("avatar_path", uploaded.path);
      await api.put("/users/me", update);
      await refresh();
      load();
    } finally { setAvatarUploading(false); }
  };

  if (!profile) {
    return <div className="min-h-[100dvh] flex items-center justify-center text-cream/40 bg-ink">Loading…</div>;
  }

  return (
    <div className="min-h-[100dvh] bg-ink pb-32" data-testid="profile-page">
      <div className="relative h-44 sm:h-56 bg-gradient-to-br from-papaya via-pink-500 to-lignum overflow-hidden">
        <div className="absolute inset-0 grain opacity-30" />
        <div className="absolute top-0 inset-x-0 jam-stripe h-1" aria-hidden />
        {isMe && (
          <div className="absolute top-4 right-4 flex gap-2">
            <Link to="/notifications" data-testid="profile-notifications" className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream">
              <ChatCircleDots size={18} />
            </Link>
            <Link to="/settings" data-testid="profile-settings" className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream">
              <Gear size={18} />
            </Link>
          </div>
        )}
        {!isMe && (
          <div className="absolute top-4 right-4">
            <button
              data-testid="profile-menu"
              onClick={() => setMenuOpen((v) => !v)}
              className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream"
            >
              <DotsThree size={20} weight="bold" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-11 crystal-glass rounded-2xl border border-white/10 overflow-hidden min-w-[160px]">
                <button
                  data-testid="profile-block"
                  onClick={toggleBlock}
                  className="w-full text-left px-4 py-3 text-sm text-red-300 hover:bg-red-500/10 flex items-center gap-2"
                >
                  <Prohibit size={16} /> Block user
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="px-5 -mt-14 relative">
        <div className="relative inline-block">
          <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-ink bg-gradient-to-br from-papaya to-sea flex items-center justify-center text-ink font-black text-4xl">
            {profile.avatar_path ? <img src={fileUrl(profile.avatar_path)} alt="" className="w-full h-full object-cover" /> : (profile.display_name || profile.username)[0]?.toUpperCase()}
          </div>
          {isMe && (
            <>
              <button data-testid="profile-avatar-edit" onClick={() => fileRef.current?.click()} className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-papaya text-ink flex items-center justify-center border-2 border-ink hover:bg-papaya-hover">
                <Camera size={16} weight="fill" />
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onAvatarPick} />
            </>
          )}
        </div>

        <h1 className="text-cream text-3xl font-black tracking-tighter mt-3 flex items-center gap-2">
          {profile.display_name}
          {profile.is_verified && <VerifiedBadge size="md" />}
          {profile.country && countries.find((c) => c.code === profile.country) && (
            <span data-testid="profile-flag" title={countries.find((c) => c.code === profile.country)?.name} className="text-2xl">
              {countries.find((c) => c.code === profile.country)?.flag}
            </span>
          )}
        </h1>
        <p className="text-cream/60 text-sm">@{profile.username}</p>
        <ProfileBadges milestones={profile.milestones_awarded} />

        {!editing ? (
          <p className="mt-3 text-cream/80 text-sm leading-relaxed max-w-md">{profile.bio || (isMe ? "Add a bio." : "")}</p>
        ) : (
          <div className="mt-3 space-y-2 max-w-md">
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Display name" data-testid="profile-edit-name" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-cream focus:outline-none focus:border-papaya" />
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Bio" rows={3} data-testid="profile-edit-bio" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-cream focus:outline-none focus:border-papaya resize-none" />
            <select
              data-testid="profile-edit-country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-cream focus:outline-none focus:border-papaya"
            >
              <option value="" className="bg-ink">Pick your island / country</option>
              {countries.map((c) => (
                <option key={c.code} value={c.code} className="bg-ink">{c.flag} {c.name}</option>
              ))}
            </select>
            <div className="flex gap-2">
              <button data-testid="profile-edit-save" onClick={saveProfile} className="px-4 py-2 rounded-full bg-papaya text-ink text-xs font-black tracking-widest uppercase">Save</button>
              <button onClick={() => setEditing(false)} className="px-4 py-2 rounded-full bg-white/5 text-cream text-xs font-black tracking-widest uppercase">Cancel</button>
            </div>
          </div>
        )}

        <div className="flex gap-6 mt-5">
          <Stat label="Posts" value={profile.posts_count} />
          <Stat label="Followers" value={profile.followers_count} />
          <Stat label="Following" value={profile.following_count} />
        </div>

        <div className="flex gap-2 mt-5">
          {isMe ? (
            !editing && (
              <button data-testid="profile-edit-toggle" onClick={() => setEditing(true)} className="flex-1 py-2.5 rounded-full bg-white/5 border border-white/10 text-cream text-xs font-black tracking-[0.2em] uppercase hover:bg-white/10 transition-colors flex items-center justify-center gap-1.5">
                <Gear size={14} /> Edit profile
              </button>
            )
          ) : (
            <>
              <button data-testid="profile-follow" onClick={toggleFollow} className={`flex-1 py-2.5 rounded-full text-xs font-black tracking-[0.2em] uppercase transition-colors ${profile.is_following ? "bg-white/5 border border-white/10 text-cream" : "bg-papaya text-ink hover:bg-papaya-hover"}`}>
                {profile.is_following ? "Following" : "Follow"}
              </button>
              <button data-testid="profile-gift" onClick={() => setGiftOpen(true)} className="flex-1 py-2.5 rounded-full bg-papaya/20 border border-papaya/40 text-papaya text-xs font-black tracking-[0.2em] uppercase hover:bg-papaya/30 transition-colors flex items-center justify-center gap-1.5">
                <Gift size={14} weight="fill" /> Gift
              </button>
              <Link to={`/messages/${profile.id}`} data-testid="profile-message" className="flex-1 py-2.5 rounded-full bg-white/5 border border-white/10 text-cream text-xs font-black tracking-[0.2em] uppercase hover:bg-white/10 transition-colors flex items-center justify-center">
                Message
              </Link>
            </>
          )}
        </div>

        {!isMe && (
          <button
            data-testid="profile-subscribe"
            onClick={subscribe}
            disabled={subBusy}
            className="mt-3 w-full py-3 rounded-full bg-gradient-to-r from-papaya via-pink-500 to-violet-500 text-ink font-black text-xs tracking-[0.2em] uppercase hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Crown size={14} weight="fill" /> Subscribe · 300 🪙 / mo
          </button>
        )}

        {isMe && (
          <div className="mt-5 grid grid-cols-4 gap-2" data-testid="profile-quick-links">
            <Link to="/wallet" data-testid="profile-to-wallet" className="flex flex-col items-center gap-1 p-3 rounded-2xl bg-white/5 border border-white/5 hover:border-papaya/40 transition-colors text-cream">
              <WalletIcon size={20} weight="duotone" className="text-papaya" />
              <span className="text-[10px] tracking-[0.15em] uppercase font-bold">Wallet</span>
            </Link>
            <Link to="/gifts" data-testid="profile-to-gifts" className="flex flex-col items-center gap-1 p-3 rounded-2xl bg-white/5 border border-white/5 hover:border-sky-400/40 transition-colors text-cream">
              <Gift size={20} weight="duotone" className="text-sky-300" />
              <span className="text-[10px] tracking-[0.15em] uppercase font-bold">Gifts</span>
            </Link>
            <Link to="/earnings" data-testid="profile-to-earnings" className="flex flex-col items-center gap-1 p-3 rounded-2xl bg-white/5 border border-white/5 hover:border-pink-400/40 transition-colors text-cream">
              <Coins size={20} weight="duotone" className="text-pink-300" />
              <span className="text-[10px] tracking-[0.15em] uppercase font-bold">Earnings</span>
            </Link>
            <Link to="/leaderboard" data-testid="profile-to-leaderboard" className="flex flex-col items-center gap-1 p-3 rounded-2xl bg-white/5 border border-white/5 hover:border-amber-400/40 transition-colors text-cream">
              <Trophy size={20} weight="duotone" className="text-amber-300" />
              <span className="text-[10px] tracking-[0.15em] uppercase font-bold">Top 20</span>
            </Link>
          </div>
        )}

        <div className="mt-8">
          <p className="text-cream/60 text-xs tracking-[0.2em] uppercase font-bold mb-3">Posts</p>
          {posts.length === 0 ? (
            <p className="text-cream/40 text-sm">No posts yet.</p>
          ) : (
            <div className="grid grid-cols-3 gap-1.5" data-testid="profile-posts-grid">
              {posts.map((p) => (
                <Link key={p.id} to={`/p/${p.id}`} className="aspect-square rounded-lg overflow-hidden bg-white/5">
                  {p.media_type === "video" ? (
                    <video src={fileUrl(p.media_path)} className="w-full h-full object-cover" muted />
                  ) : (
                    <img src={fileUrl(p.media_path)} alt="" className="w-full h-full object-cover" />
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {giftOpen && profile && (
        <GiftModal
          recipient={profile}
          onClose={() => setGiftOpen(false)}
          onSent={() => { setGiftOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-cream font-black text-xl tracking-tighter">{value || 0}</p>
      <p className="text-cream/50 text-[10px] tracking-[0.2em] uppercase font-bold">{label}</p>
    </div>
  );
}
